import { HeaderProps } from "./Header/types";
import { TeamRankingsProps, TeamRankingType } from "./TeamRankings/types";
import {
  getColorFromPercentile,
  getRampOutline,
  nth,
  seasonTypeLabel,
} from "../utils";
import { useLeagueLogo } from "../../data/LeagueEnvironmentProvider";
import GameCard from "./Header/GameCard";
import RankPercentileLegend from "./Header/RankPercentileLegend";
import { PreliminaryGamesNote, PreliminaryTag } from "../preliminary";
import { windowRange } from "../reportWindow";
import "../teamReportDesign.scss";

// Season string ("2025") → "2025-26"
const seasonLabel = (season?: number) =>
  season ? `${season}-${String((season + 1) % 100).padStart(2, "0")}` : "";

/** One record box: the record and conference rank, its 82-game pace, the net rating. The box names itself on its top
 * border (Actual / Expected) instead of a footer line, so both boxes keep the same rows (Trevor, 2026-11-04). */
function RecordBox({
  label,
  wins,
  losses,
  netRating,
  standing,
  conference,
  isTied,
  expected,
  winsPer82,
}: {
  label: string;
  wins: number;
  losses: number;
  netRating: number;
  /** Absent on playoff and preseason reports: a plain record, no conference standing. */
  standing?: number;
  conference?: string;
  isTied?: boolean;
  expected?: boolean;
  /** The record's share over a full season (regular season and its windows only) */
  winsPer82?: number | null;
}) {
  const net = `${netRating > 0 ? "+" : ""}${netRating.toFixed(1)}`;
  return (
    <div className={expected ? "tr-rec x" : "tr-rec"}>
      <span className="lg">{label}</span>
      <div className="v">
        {wins}-{losses}
        {standing != null && (
          <span className="st">
            {nth(standing, isTied)} {conference}
          </span>
        )}
      </div>
      {winsPer82 != null && (
        <div className="s">
          <b>
            {winsPer82}-{82 - winsPer82}
          </b>{" "}
          82-game pace
        </div>
      )}
      <div className="s">
        {net} {expected ? "xNet Rating" : "Net Rating"}
      </div>
    </div>
  );
}

/** A quadrant's big chip: season reports carry a rank (1 = best), coloured by its bucket and printed "2nd"; game
 * reports a percentile. Shared by the band's breakdown strip and the ownership quadrants. */
export function rankingChip(ranking: TeamRankingType, isSingleGame?: boolean) {
  const population = ranking.rankPopulation ?? 30;
  const percentile = isSingleGame ? ranking.rankOrPercentile : undefined;
  const rank = isSingleGame ? undefined : ranking.rankOrPercentile;
  return {
    text: isSingleGame ? String(Math.round(ranking.rankOrPercentile)) : nth(ranking.rankOrPercentile),
    style: {
      background: getColorFromPercentile(percentile, rank, population),
      boxShadow: getRampOutline(percentile, rank, population),
    },
  };
}

function BreakdownCell({
  label,
  ranking,
  isSingleGame,
}: {
  label: string;
  ranking: TeamRankingType;
  isSingleGame?: boolean;
}) {
  const chip = rankingChip(ranking, isSingleGame);
  return (
    <div className="cell">
      <span className="chip big" style={chip.style}>
        {chip.text}
      </span>
      <div className="lbl">{label}</div>
    </div>
  );
}

/** The four-quadrant breakdown strip (1A). Exported so a page can place it
 * apart from the band (the ownership page puts it under its Overview). */
export function BreakdownStrip({
  rankingsProps,
  isSingleGame = false,
}: {
  rankingsProps: TeamRankingsProps;
  isSingleGame?: boolean;
}) {
  return (
    <div className="bk">
      <BreakdownCell
        label="First-Shot HC Offense"
        ranking={rankingsProps.hcOffense}
        isSingleGame={isSingleGame}
      />
      <BreakdownCell
        label="Off Rebounding + Trans D"
        ranking={rankingsProps.trDefense}
        isSingleGame={isSingleGame}
      />
      <BreakdownCell
        label="First-Shot HC Defense"
        ranking={rankingsProps.hcDefense}
        isSingleGame={isSingleGame}
      />
      <BreakdownCell
        label="Def Rebounding + Trans O"
        ranking={rankingsProps.trOffense}
        isSingleGame={isSingleGame}
      />
    </div>
  );
}

/**
 * The 1A team header band: framed grayscale logo + city/name identity, the
 * season scope line, actual + expected record boxes (season) or the game
 * card (single game), a faint watermark, and the four-quadrant breakdown
 * strip that replaces the old TeamRankings chevron row.
 */
export default function TeamHeaderBand({
  headerProps,
  rankingsProps,
  isSingleGame = false,
  scope,
  reportLabel,
  productLabel = "Team Report",
  stamp,
  showBreakdown = true,
  cardNote,
  showPreliminary = true,
}: {
  headerProps: HeaderProps;
  rankingsProps: TeamRankingsProps;
  isSingleGame?: boolean;
  /** Scope-line override (the ownership page names the game's own season and
   * type; the default reads the report's season, which game blobs lack) */
  scope?: string[];
  /** Right-hand label in the .ph strip (default "Game Report" / "Overview") */
  reportLabel?: string;
  /** Left-hand product name in the .ph strip */
  productLabel?: string;
  /** Replaces the red "As of <build date>" stamp (the ownership page shows
   * the game date) */
  stamp?: string;
  /** Render the breakdown strip under the band (off when the page places
   * BreakdownStrip itself) */
  showBreakdown?: boolean;
  /** Single game: a line under the score (challenges, garbage time) */
  cardNote?: React.ReactNode;
  /** The Preliminary tag / note on a delayed-feed report. Off on the ownership view: Trevor decides whether a
   * preliminary packet goes out, and a delivered one carries no tag (2026-10-26) */
  showPreliminary?: boolean;
}) {
  const {
    teamId,
    teamName,
    teamNickname,
    teamAbbrev,
    season,
    season_type,
    conferenceStandingsActual,
    conferenceStandingsExpected,
    gameInfo,
    asOfDate,
    feed,
    delayedFeedGames,
    window,
  } = headerProps;
  const getTeamLogoUrl = useLeagueLogo();
  const logo = getTeamLogoUrl(teamId);
  // 82-game paces (2026-11-04): the actual record's and the expected (unrounded pythagorean) share's. A full-season
  // pace means nothing for playoffs or preseason.
  const showPace = season_type !== "playoff" && season_type !== "preseason";
  const per82 = (share?: number | null) => (showPace && share != null ? Math.round(share * 82) : null);
  const actualGames = conferenceStandingsActual
    ? conferenceStandingsActual.wins + conferenceStandingsActual.losses
    : 0;
  const actualPer82 = per82(actualGames ? conferenceStandingsActual!.wins / actualGames : null);
  const expectedPer82 = per82(conferenceStandingsExpected?.winPct);
  const teamCity = teamName.replace(teamNickname, "").trim();
  const gamesPlayed = conferenceStandingsActual
    ? conferenceStandingsActual.wins + conferenceStandingsActual.losses
    : undefined;
  // A window report reads as its window: "2025-26 | Last 10 | Mar 23 – Apr 12 | 10 GP"
  const scopeParts = (
    window
      ? [seasonLabel(season), window.label, windowRange(window), `${window.games} GP`]
      : [
          seasonLabel(season),
          seasonTypeLabel(season_type),
          gamesPlayed !== undefined ? `${gamesPlayed} GP` : undefined,
        ]
  ).filter(Boolean) as string[];
  const scopeLine = scope ?? scopeParts;

  return (
    <div className="trd">
      {/* Legend rides the center of the .ph strip (vertical-space saver —
          the separate Overall header row above the table is gone) */}
      <div className="ph" style={{ alignItems: "center" }}>
        <div className="rpt" style={{ flex: 1 }}>
          {seasonLabel(season)} {productLabel}
          {stamp ? (
            <span className="yr"> · {stamp}</span>
          ) : (
            asOfDate && <span className="yr"> · As of {asOfDate}</span>
          )}
          {showPreliminary && feed === "delayed" && <PreliminaryTag />}
        </div>
        <RankPercentileLegend
          rankPopulation={rankingsProps.hcOffense.rankPopulation}
        />
        <div className="who" style={{ flex: 1, textAlign: "right" }}>
          {teamName} ·{" "}
          {reportLabel ?? (isSingleGame ? "Game Report" : "Overview")}
        </div>
      </div>
      {showPreliminary && !isSingleGame && !!delayedFeedGames?.length && (
        <div style={{ margin: "-4px 0 6px" }}>
          <PreliminaryGamesNote games={delayedFeedGames} />
        </div>
      )}
      <div className="tr-band">
        <img className="wm" src={logo} alt="" />
        <div className="tr-logo">
          <img src={logo} alt={teamName} />
        </div>
        <div className="id">
          <div className="city">{teamCity}</div>
          <div className="nm">{teamNickname}</div>
          <div className="scope">
            {scopeLine.map((part, i) => (
              <span key={part}>
                {i > 0 && <span className="sep">|</span>}
                {part}
              </span>
            ))}
          </div>
        </div>
        {gameInfo ? (
          <div style={{ marginLeft: "auto", position: "relative", zIndex: 1 }}>
            <GameCard
              gameInfo={gameInfo}
              teamAbbrev={teamAbbrev}
              teamId={teamId}
            />
            {cardNote && <div className="gnote">{cardNote}</div>}
          </div>
        ) : (
          <div className="tr-recs">
            {conferenceStandingsActual && (
              <RecordBox {...conferenceStandingsActual} label="Actual" winsPer82={actualPer82} />
            )}
            {conferenceStandingsExpected && (
              <RecordBox
                {...conferenceStandingsExpected}
                label="Expected"
                expected
                winsPer82={expectedPer82}
              />
            )}
          </div>
        )}
      </div>
      {showBreakdown && (
        <BreakdownStrip rankingsProps={rankingsProps} isSingleGame={isSingleGame} />
      )}
    </div>
  );
}
